import { useQuery, useQueryClient } from '@tanstack/react-query';
import { update } from 'ramda';
import { useMemo, useCallback } from 'react';

import { getSelectionAll } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import { getSelectionWithWoltSpecialsAsFirstCategory, isSharedMenuBootstrapping } from 'modules/shared-menu/utils';

import { useSelectionInfoContext } from '../contexts/SelectionInfoContext';
import { Offer, FullSelection, Option } from '../pages/SharedMenuEditorPage/types';
import { SharedMenu, OfferingPlatformScope } from '../types';

type UpdateOfferAction = {
  actionType: 'updateOffer';
  payload: Offer;
};

type UpdateOptionAction = {
  actionType: 'updateOption';
  payload: Option;
};

type UpdateSelectionAction = {
  actionType: 'updateSelection';
  payload: SharedMenu;
  preventManualStatusUpdate?: boolean;
};

type UpdateSelectionContainerAction = {
  actionType: 'updateSelectionContainer';
  payload: FullSelection;
};

type AddOfferAction = {
  actionType: 'addOffer';
  payload: Offer;
};

type UpdateAllOffersAction = {
  actionType: 'updateAllOffers';
  payload: Offer[];
};

type AddOptionAction = {
  actionType: 'addOption';
  payload: Option;
};

type UpdateCategoriesAction = {
  actionType: 'updateCategories';
  payload: FullSelection['selection']['categories'];
};

export type UpdateSelectionDataAction =
  | AddOfferAction
  | AddOptionAction
  | UpdateOfferAction
  | UpdateAllOffersAction
  | UpdateOptionAction
  | UpdateSelectionAction
  | UpdateSelectionContainerAction
  | UpdateCategoriesAction;

export const buildGetSelectionAllQueryKey = (selectionId: string, includeDraft: boolean) => [
  'get-selection-all',
  selectionId,
  String(includeDraft),
];

type UseLoadSelectionDataArgs = {
  selectionId: string;
  scope: OfferingPlatformScope;
  enabled?: boolean;
};

export const useLoadSelectionData = ({ selectionId, scope, enabled = true }: UseLoadSelectionDataArgs) => {
  const includeDraft = scope.type !== 'venue';
  const queryKey = useMemo(() => buildGetSelectionAllQueryKey(selectionId, includeDraft), [includeDraft, selectionId]);
  const {
    data: selectionData,
    isInitialLoading: loadingInitialSelectionData,
    error: selectionDataError,
  } = useQuery(queryKey, () => getSelectionAll(selectionId, false), {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled,
    select: getSelectionWithWoltSpecialsAsFirstCategory,
  });

  return {
    loadingInitialSelectionData,
    selectionData,
    selectionDataError,
  };
};

export const useSelectionData = () => {
  const queryClient = useQueryClient();
  const { selection, vlmeConfig } = useSelectionInfoContext();
  const scope = useScopeContext();
  const { updateSharedMenuStatus } = useSharedMenuData({ scope });

  const includeDraft = scope.type !== 'venue';
  const queryKey = useMemo(
    () => buildGetSelectionAllQueryKey(selection.id, includeDraft),
    [selection.id, includeDraft],
  );

  const {
    data: selectionData,
    isFetching: loadingSelectionData,
    isInitialLoading: loadingInitialSelectionData,
    error: selectionDataError,
    refetch: refetchSelectionData,
  } = useQuery(queryKey, () => getSelectionAll(selection.id, includeDraft), {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    refetchInterval: data => {
      if (isSharedMenuBootstrapping(data?.selection.draft)) {
        return 1000;
      } else {
        return false;
      }
    },
    select: getSelectionWithWoltSpecialsAsFirstCategory,
  });

  const setSelectionStatusToDraft = useCallback(() => {
    updateSharedMenuStatus(selection.id, {});

    queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
      if (!oldData) {
        throw new Error('No menu data fetched, this should not happen!');
      }

      return {
        ...oldData,
        selection: {
          ...oldData.selection,
          draft: {},
        },
      };
    });
  }, [selection.id, updateSharedMenuStatus, queryClient, queryKey]);

  const handleSelectionStatusUpdate = useCallback(() => {
    const isSharedMenu = !vlmeConfig;
    const latestSelectionData: FullSelection | undefined = queryClient.getQueryData(queryKey);
    const isCurrentSelectionStatusLive = !!latestSelectionData && !latestSelectionData?.selection.draft;

    if (isSharedMenu && isCurrentSelectionStatusLive) {
      setSelectionStatusToDraft();
    }
  }, [setSelectionStatusToDraft, vlmeConfig, queryClient, queryKey]);

  const updateSelectionContainer = useCallback(
    (updatedFullSelection: FullSelection) => {
      queryClient.setQueryData(queryKey, updatedFullSelection);
      updateSharedMenuStatus(updatedFullSelection.selection.id, updatedFullSelection.selection.draft);
    },
    [queryClient, queryKey, updateSharedMenuStatus],
  );

  const addOffer = useCallback(
    (newOffer: Offer) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const updatedOffers = [...oldData.offers, newOffer];
        return { ...oldData, offers: updatedOffers };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateOffer = useCallback(
    (updatedOffer: Offer) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const itemIndex = oldData.offers.findIndex(item => item.id === updatedOffer.id);
        const updatedOffers = update(itemIndex, updatedOffer, oldData.offers);
        return { ...oldData, offers: updatedOffers };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateAllOffers = useCallback(
    (updatedOffers: Offer[]) => {
      queryClient.setQueryData(queryKey, (currentSelection: FullSelection | undefined) => {
        if (!currentSelection) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        return { ...currentSelection, offers: updatedOffers };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const addOption = useCallback(
    (newOption: Option) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const updatedOptions = [...oldData.options, newOption];
        return {
          ...oldData,
          options: updatedOptions,
        };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateOption = useCallback(
    (updatedOption: Option) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const optionIndex = oldData.options.findIndex(o => o.id === updatedOption.id);
        const updatedOptions = update(optionIndex, updatedOption, oldData.options);
        return {
          ...oldData,
          options: updatedOptions,
        };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateSelection = useCallback(
    (updatedSelection: SharedMenu, preventManualStatusUpdate?: boolean) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => ({
        ...oldData,
        selection: updatedSelection,
      }));

      if (!preventManualStatusUpdate) {
        handleSelectionStatusUpdate();
      }
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateCategories = useCallback(
    (updatedCategories: FullSelection['selection']['categories']) => {
      queryClient.setQueryData(queryKey, (oldData: FullSelection | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const updatedSelection: SharedMenu = {
          ...oldData?.selection,
          categories: updatedCategories,
        };

        return {
          ...oldData,
          selection: updatedSelection,
        };
      });

      handleSelectionStatusUpdate();
    },
    [queryClient, queryKey, handleSelectionStatusUpdate],
  );

  const updateSelectionData = useCallback(
    (action: UpdateSelectionDataAction) => {
      switch (action.actionType) {
        case 'updateOffer':
          updateOffer(action.payload);
          return;
        case 'updateAllOffers':
          updateAllOffers(action.payload);
          return;
        case 'updateOption':
          updateOption(action.payload);
          return;
        case 'updateSelection':
          updateSelection(action.payload, action.preventManualStatusUpdate);
          return;
        case 'updateSelectionContainer':
          updateSelectionContainer(action.payload);
          return;
        case 'addOffer':
          addOffer(action.payload);
          return;
        case 'addOption':
          addOption(action.payload);
          return;
        case 'updateCategories':
          updateCategories(action.payload);
          return;
        default:
          return;
      }
    },
    [
      updateOffer,
      updateAllOffers,
      updateOption,
      updateSelection,
      updateSelectionContainer,
      addOffer,
      addOption,
      updateCategories,
    ],
  );

  const resetDraftSelectionQuery = useCallback(() => {
    queryClient.resetQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    categories: selectionData?.selection.categories ?? [],
    loadingInitialSelectionData,
    loadingSelectionData,
    offers: selectionData?.offers ?? [],
    options: selectionData?.options ?? [],
    refetchSelectionData,
    resetDraftSelectionQuery,
    selection: selectionData?.selection ?? selection,
    selectionDataError,
    updateSelectionData,
  };
};
